import React from 'react';
import { UploadCloud, FileSpreadsheet, RefreshCw } from 'lucide-react';
import { CSVPreview } from '../../components/calibrationModal/CSVPreview';
import type { UseBulkUploadReturn } from '../../components/calibrationModal/hooks/useBulkUpload';

interface UploadCsvStepProps {
  bulkUpload: UseBulkUploadReturn;
}

export const UploadCsvStep: React.FC<UploadCsvStepProps> = ({ bulkUpload }) => {
  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-4">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[360px_minmax(0,1fr)]">
        <div className="space-y-4 rounded-xl border border-blue-200 bg-white p-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-blue-800">
              Upload CSV file
            </label>

            <div className="rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/60 p-5 text-center">
              <input
                ref={bulkUpload.fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={bulkUpload.handleFileSelect}
              />

              <UploadCloud className="mx-auto mb-3 h-8 w-8 text-blue-600" />
              <p className="mb-2 text-sm font-medium text-gray-700">
                Drag and drop or browse a CSV file
              </p>
              <button
                type="button"
                onClick={() => bulkUpload.fileInputRef.current?.click()}
                className="btn-primary"
              >
                Choose file
              </button>
            </div>
          </div>

          {bulkUpload.selectedFiles[0] && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-green-700">
                Selected file
              </p>

              <div className="mt-2 flex items-center gap-3">
                {/* Filename container */}
                <div className="flex min-w-0 flex-1 items-center gap-2 text-sm text-gray-800">
                  <FileSpreadsheet className="h-4 w-4 shrink-0 text-blue-600" />

                  <span
                    className="min-w-0 truncate"
                    title={bulkUpload.selectedFiles[0].name}
                  >
                    {bulkUpload.selectedFiles[0].name}
                  </span>
                </div>

                {/* Refresh button */}
                <button
                  type="button"
                  onClick={() => {
                    bulkUpload.clearFiles();
                    bulkUpload.fileInputRef.current?.click();
                  }}
                  className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-900"
                >
                  <RefreshCw className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          <div>
            <label
              htmlFor="bulk-processing-name"
              className="mb-2 block text-sm font-medium text-blue-800"
            >
              Equipment Calibration Certificate Number{' '}
              <span className="text-red-500">*</span>
            </label>
            <input
              id="bulk-processing-name"
              type="text"
              value={bulkUpload.equipmentCalibrationCertificateNo}
              onChange={(event) =>
                bulkUpload.setEquipmentCalibrationCertificateNo(
                  event.target.value,
                )
              }
              placeholder="Certificate Number"
              className="input w-full h-10 text-sm px-3"
            />
          </div>
        </div>

        <div className="rounded-xl border border-blue-200 bg-white p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h4 className="text-sm font-semibold text-gray-800">CSV preview</h4>
          </div>

          {!bulkUpload.selectedFiles[0] ? (
            <div className="flex min-h-[400px] items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 text-center text-sm text-gray-500">
              Upload a CSV file to preview rows and columns before processing.
            </div>
          ) : (
            <CSVPreview
              csvPreviewData={bulkUpload.csvPreviewData}
              columnOrder={bulkUpload.csvColumnOrder}
              showPreview={bulkUpload.showPreview}
              onTogglePreview={() => bulkUpload.setShowPreview((prev) => !prev)}
            />
          )}
        </div>
      </div>
    </div>
  );
};
